import React from "react";
import styles from "./W1Page.module.css";

/* ===== Quantidade (inteiro) ===== */
const FMT_INT_BR = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });

function parseQtdIntBR(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;

  if (/^\d+$/.test(s)) return Number(s);
  if (/^\d{1,3}(\.\d{3})+$/.test(s)) return Number(s.replace(/\./g, ""));

  if (/^\d+\.\d+$/.test(s)) {
    const [a, b] = s.split(".");
    if (/^0+$/.test(b)) return Number(a);
    return null;
  }

  if (!s.includes(".")) {
    const digits = s.replace(/[^\d]/g, "");
    return digits ? Number(digits) : null;
  }

  return null;
}

function formatQtdIntBR(n) {
  if (!Number.isFinite(n)) return "";
  return FMT_INT_BR.format(n);
}

/** ✅ CHAVES QUE NÃO DEVEM APARECER NA TABELA */
const HIDDEN_KEYS = new Set([
  "codigo_unico",
  "unidade_de_medida",
  "equipamento",
  "nome_do_responsavel",
  "telefone_do_reponsavel",
]);

/* Colunas base (WF_1) */
const COLUNAS_BASE = [
  { key: "codigo_unico", label: "Código Único" }, // readonly (OCULTO)
  { key: "data", label: "Data", type: "date" },
  { key: "atividade", label: "Atividade" },
  { key: "unidade_de_medida", label: "Unidade" }, // (OCULTO)
  { key: "ativo", label: "Ativo" },
  { key: "equipamento", label: "Equipamento" }, // (OCULTO)
  { key: "tempo_do_intervalo", label: "Tempo do Intervalo", type: "time" },
  { key: "sub", label: "Sub" },
  { key: "sbOrigem", label: "Pátio de Origem" },
  { key: "sbDeTrabalho", label: "SB de Trabalho" },
  { key: "kmInicio", label: "KM Início", type: "number" },
  { key: "kmFinal", label: "KM Final", type: "number" },
  { key: "sbEncerramento", label: "Pátio de Encerramento" },
  { key: "quantidade", label: "Quantidade" }, // máscara
  { key: "observacao", label: "Observação" },
  { key: "nome_do_responsavel", label: "Resp. Nome" }, // (OCULTO)
  { key: "telefone_do_reponsavel", label: "Resp. Telefone" }, // (OCULTO)
];

function getColunas(exibeOS) {
  const base = [...COLUNAS_BASE];

  if (exibeOS) {
    const idxAtivo = base.findIndex((x) => x.key === "ativo");
    base.splice(idxAtivo + 1, 0, { key: "os", label: "OS" });
  }

  // ✅ aqui que some da tela
  return base.filter((c) => !HIDDEN_KEYS.has(c.key));
}

const COL_CLASS = {
  codigo_unico: styles.colCodigo,
  data: styles.colData,
  atividade: styles.colAtividade,
  unidade_de_medida: styles.colUm,
  ativo: styles.colAtivo,
  equipamento: styles.colEquip,
  tempo_do_intervalo: styles.colTempo,
  sub: styles.colSub,
  sbOrigem: styles.colPatioOrigem,
  sbDeTrabalho: styles.colSbTrabalho,
  kmInicio: styles.colKm,
  kmFinal: styles.colKm,
  sbEncerramento: styles.colPatioEncerramento,
  quantidade: styles.colQuantidade,
  observacao: styles.colObservacao,
  os: styles.colOs,
  nome_do_responsavel: styles.colRespNome,
  telefone_do_reponsavel: styles.colRespTel,
};

export default function W1Table({
  linhas,
  setLinhas,
  atividades,
  ativos,
  subs,
  allSBs,
  onDuplicar,
  onRemover,
}) {
  function handleChange(id, key, value) {
    setLinhas((ant) =>
      ant.map((linha) => {
        if (linha.id !== id) return linha;

        // mantém unidade no state (mesmo oculta)
        if (key === "atividade") {
          const atividadeSelecionada = atividades.find((a) => a.atividade === value);
          return {
            ...linha,
            atividade: value,
            unidade_de_medida: atividadeSelecionada ? atividadeSelecionada.unidade : "",
          };
        }

        if (key === "sub") {
          return { ...linha, sub: value, kmValido: true, kmMsg: "", sbDeTrabalho: "" };
        }

        return { ...linha, [key]: value };
      })
    );
  }

  function renderInput(col, linha) {
    // como filtramos, isso nem é chamado pros ocultos,
    // mas deixei por segurança:
    if (HIDDEN_KEYS.has(col.key)) return null;

    if (col.key === "atividade") {
      return (
        <>
          <input
            list={`datalist-atividade-${linha.id}`}
            value={linha.atividade || ""}
            onChange={(e) => handleChange(linha.id, "atividade", e.target.value)}
            placeholder="Digite ou selecione"
            autoComplete="off"
          />
          <datalist id={`datalist-atividade-${linha.id}`}>
            {atividades.map((atv, idx) => (
              <option key={idx} value={atv.atividade} />
            ))}
          </datalist>
        </>
      );
    }

    if (col.key === "ativo") {
      return (
        <>
          <input
            list={`datalist-ativo-${linha.id}`}
            value={linha.ativo || ""}
            onChange={(e) => handleChange(linha.id, "ativo", e.target.value)}
            placeholder="Digite ou selecione"
            autoComplete="off"
          />
          <datalist id={`datalist-ativo-${linha.id}`}>
            {ativos.map((atv, idx) =>
              typeof atv === "object" ? (
                <option key={idx} value={atv.ativos} />
              ) : (
                <option key={idx} value={atv} />
              )
            )}
          </datalist>
        </>
      );
    }

    if (col.key === "sub") {
      return (
        <select
          value={linha.sub || ""}
          onChange={(e) => handleChange(linha.id, "sub", e.target.value)}
          disabled={!subs.length}
        >
          <option value="" style={{ display: "none" }}></option>
          {subs.map((s) => (
            <option key={s.subdivisao_id} value={s.subdivisao_id}>
              {s.subdivisao_id}
            </option>
          ))}
        </select>
      );
    }

    if (["sbOrigem", "sbEncerramento"].includes(col.key)) {
      return (
        <>
          <input
            list={`datalist-sb-${col.key}-${linha.id}`}
            value={linha[col.key] || ""}
            onChange={(e) => handleChange(linha.id, col.key, e.target.value)}
            placeholder="Digite ou selecione"
            autoComplete="off"
          />
          <datalist id={`datalist-sb-${col.key}-${linha.id}`}>
            {allSBs.map((sb, idx) => (
              <option key={idx} value={sb} />
            ))}
          </datalist>
        </>
      );
    }

    if (col.key === "sbDeTrabalho") {
      return (
        <input
          type="text"
          value={linha.sbDeTrabalho || ""}
          readOnly
          className={styles.readonly}
          placeholder="Preenchido pelo KM"
        />
      );
    }

    if (col.key === "kmInicio" || col.key === "kmFinal") {
      return (
        <div className={styles.kmField}>
          <input
            type="number"
            value={linha[col.key] || ""}
            onChange={(e) => handleChange(linha.id, col.key, e.target.value)}
            className={!linha.kmValido ? styles.kmInvalid : ""}
          />
          {!linha.kmValido && (
            <span className={`${styles.kmMsg} ${styles.kmErr}`}>{linha.kmMsg}</span>
          )}
        </div>
      );
    }

    if (col.key === "quantidade") {
      return (
        <input
          type="text"
          inputMode="numeric"
          value={linha.quantidade || ""}
          placeholder="Ex: 1 ou 1000"
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d.]/g, "");
            handleChange(linha.id, "quantidade", v);
          }}
          onBlur={(e) => {
            const n = parseQtdIntBR(e.target.value);
            if (n == null) return;
            handleChange(linha.id, "quantidade", formatQtdIntBR(n)); // 1000 -> 1.000
          }}
        />
      );
    }

    if (col.key === "os") {
      if ((linha.atividade || "").trim().toLowerCase() !== "socadora") {
        return <span className={styles.osPlaceholder}>-</span>;
      }
      return (
        <input
          type="text"
          value={linha.os || ""}
          onChange={(e) => handleChange(linha.id, "os", e.target.value)}
          placeholder="Digite OS"
        />
      );
    }

    return (
      <input
        type={col.type || "text"}
        value={linha[col.key] || ""}
        onChange={(e) => handleChange(linha.id, col.key, e.target.value)}
      />
    );
  }

  const exibeOS = linhas.some((l) => (l.atividade || "").trim().toLowerCase() === "socadora");
  const cols = getColunas(exibeOS);

  return (
    <div className={styles.card}>
      <div className={styles.tableArea}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.idCol}>ID</th>
              {cols.map((col) => (
                <th key={col.key} className={COL_CLASS[col.key] || ""}>
                  {col.label}
                </th>
              ))}
              <th className={styles.actionsCol}>Ações</th>
            </tr>
          </thead>

          <tbody>
            {linhas.map((linha, idx) => (
              <tr key={linha.id}>
                <td className={styles.idCol}>{idx + 1}</td>

                {cols.map((col) => (
                  <td key={col.key} className={COL_CLASS[col.key] || ""}>
                    {renderInput(col, linha)}
                  </td>
                ))}

                <td className={styles.actionsCol}>
                  <button
                    title="Duplicar linha"
                    onClick={() => onDuplicar(linha.id)}
                    className={styles.btnDup}
                  >
                    ⎘
                  </button>
                  <button
                    title="Remover linha"
                    onClick={() => onRemover(linha.id)}
                    disabled={linhas.length === 1}
                    className={styles.btnX}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>

        </table>
      </div>
    </div>
  );
}
